// src/utils/evalArr.js
import { splitConditionalExpression, stripOuterParentheses } from "../scriptsPage/quantityRuleUtils";
import { splitVectorParts, unwrapVector } from "../scriptsPage/vectorRuleUtils";
export function resolveSettingValue(settingsArr, key, depth = 0) {
  if (depth > 6) return "0"; // ciklusvédelem
  const hit =
    settingsArr.find((x) => String(x.settingId) === String(key)) || null;
  if (!hit) return "0";
  const raw = String(hit.value);

  // ha számnak látszik, visszaadjuk számként
  const num = Number(raw);
  if (Number.isFinite(num) && String(num) === raw) return String(num);

  // ha maga is makró: "(*123*)" vagy "(*123@2*)"
  const m = raw.match(/^\(\*(\d+)(?:@(\d+))?\*\)$/);
  if (m) {
    const ref = m[2] ? `${m[1]}@${m[2]}` : m[1];
    return resolveSettingValue(settingsArr, ref, depth + 1);
  }

  // egyébként string-érték
  return JSON.stringify(raw);
}

/**
 * Evaluate a “[x,y,z]”-style expression, substituting width/height/depth
 * and (*id*) macros. Returns [x,y,z].
 */
export function evalArr(
  raw,
  overrideSettings = null,
  overrideDims = null,
  settingsList = [],
  userDimensions = {}
) {
  const asStr = String(raw || "").trim();

  function toSettingsArray(src) {
    if (!src) return [];
    if (Array.isArray(src)) return src;
    if (typeof src === "object") {
      return Object.entries(src).map(([settingId, value]) => ({
        settingId: String(settingId),
        value: String(value)
      }));
    }
    if (typeof src === "string") {
      // "1:a,2:b" formátum
      return src
        .split(",")
        .map((p) => p.split(":").map((s) => s.trim()))
        .filter(([k, v]) => k && v)
        .map(([settingId, value]) => ({ settingId, value }));
    }
    return [];
  }
  // --- HELPERS: refScriptId feloldása (szám, makró, kifejezés) ---
  function resolveRefScriptId(refRaw, cfg) {
    if (refRaw == null || refRaw === "") return null;
    let raw = String(refRaw).trim();

    // külső idézők lecsupaszítása
    if (
      (raw.startsWith('"') && raw.endsWith('"')) ||
      (raw.startsWith("'") && raw.endsWith("'"))
    ) {
      raw = raw.slice(1, -1);
    }

    // tiszta szám?
    if (/^\d+$/.test(raw)) return raw;

    // tiszta makró?  (*71*) vagy (*71@2*)
    const m = raw.match(/^\(\*(\d+(?:@\d+)?)\*\)$/);
    if (m) {
      const settingsArr = Object.entries(cfg).map(([settingId, value]) => ({
        settingId: String(settingId),
        value: String(value)
      }));
      let resolved = resolveSettingValue(settingsArr, m[1]); // lehet "103" vagy "\"103\""
      try {
        resolved = JSON.parse(resolved);
      } catch {}
      return /^\d+$/.test(String(resolved)) ? String(resolved) : null;
    }

    // általános kifejezés (pl. (*71*)==103?104:105)
    try {
      const expr = evaluateFormula(raw, cfg);
      const v = new Function(`return (${expr});`)();
      return v != null ? String(v) : null;
    } catch {
      return null;
    }
  }

  // Csak akkor delegáljunk expandIterated-re, ha TOP-LEVEL [x,y,z],
  // és a három tengely valamelyike közvetlenül i(...) (nincs ternary, nincs beágyazott lista).
  if (asStr.startsWith("[") && asStr.endsWith("]")) {
    const innerTop = asStr.slice(1, -1);
    const partsTop = splitTopLevel(innerTop); // top-level szétvágás

    const isPureIMacro = (s) => /^\s*i\([^?[\]]*\)\s*$/.test(s.trim());
    const hasPureIMacro = partsTop.some(isPureIMacro);
    const hasNestedOrTernary = partsTop.some(
      (p) => p.includes("?") || /^\s*\[/.test(p.trim())
    );

    if (hasPureIMacro && !hasNestedOrTernary) {
      const coords = expandIterated(
        partsTop,
        1,
        overrideSettings || settingsList,
        overrideDims || [
          userDimensions.width,
          userDimensions.height,
          userDimensions.depth
        ]
      );
      return coords[0] || [0, 0, 0];
    }
  }

  const settingsToUse = overrideSettings || settingsList;
  const settingsArr = toSettingsArray(overrideSettings || settingsList);
  const {
    width = 0,
    height = 0,
    depth = 0
  } = overrideDims
    ? {
        width: overrideDims[0],
        height: overrideDims[1],
        depth: overrideDims[2]
      }
    : userDimensions;

  if (Array.isArray(raw)) {
    return raw.slice(0, 3).map((v) => Number(v) || 0);
  }
  const str = String(raw || "").trim();
  if (!str.startsWith("[") || !str.endsWith("]")) {
    return [0, 0, 0];
  }
  // strip [ … ]
  const inner = str.slice(1, -1);
  // split top-level by commas
  // split top-level by commas, tracking both () and []
  const parts = [];
  let depthPar = 0,
    depthBr = 0,
    last = 0;
  for (let i = 0; i < inner.length; i++) {
    const ch = inner[i];
    if (ch === "(") depthPar++;
    else if (ch === ")") depthPar--;
    else if (ch === "[") depthBr++;
    else if (ch === "]") depthBr--;
    else if (ch === "," && depthPar === 0 && depthBr === 0) {
      parts.push(inner.slice(last, i).trim());
      last = i + 1;
    }
  }
  parts.push(inner.slice(last).trim());

  return parts.slice(0, 3).map((expr0) => {
    let expr = expr0.replace(/^[\[\]]+|[\[\]]+$/g, "").trim();
    if (!expr) return 0;
    // substitute macros
    expr = expr
      .replace(/\bwidth\b/g, width)
      .replace(/\bheight\b/g, height)
      .replace(/\bdepth\b/g, depth)
      /*.replace(/\(\*(\d+)\*\)/g, (_, id) => {
        const s = settingsToUse.find((x) => String(x.settingId) === id);
        if (!s) return "0";
        const n = Number(s.value);
        // ha numerikus string, akkor számmá konvertáljuk
        return Number.isFinite(n) && String(n) === String(s.value)
          ? n
          : JSON.stringify(s.value);
      });*/
      .replace(/\(\*(\d+(?:@\d+)?)\*\)/g, (_, key) => {
        return resolveSettingValue(settingsArr, key);
      });

    try {
      // eslint-disable no-new-func
      const val = new Function(`return (${expr})`)();
      return Number(val) || 0;
    } catch {
      return 0;
    }
  });
}
/*
function splitIMacroArgs(str) {
  // str === "i(...)" bemenet
  const inner = str.slice(2, -1);
  let depth = 0;
  for (let i = 0; i < inner.length; i++) {
    const ch = inner[i];
    if (ch === "(") depth++;
    else if (ch === ")") depth--;
    else if (ch === "," && depth === 0) {
      return [
        inner.slice(0, i).trim(), // startExpr
        inner.slice(i + 1).trim() // endExpr
      ];
    }
  }
  // ha nem találunk vesszőt, visszaadunk mindkettőt ugyanúgy
  return [inner, inner];
}
*/

function splitIMacroArgs(str) {
  const inner = str.slice(2, -1);
  const parts = splitTopLevel(inner).map((s) => s.trim());
  return parts;
}
/*
export function expandIterated(rawParts, count, parentSettings, parentSize) {
  let settingsArr;
  if (Array.isArray(parentSettings)) {
    settingsArr = parentSettings;
  } else if (parentSettings && typeof parentSettings === "object") {
    settingsArr = Object.entries(parentSettings)
      .filter(([k]) => !["width", "height", "depth"].includes(k))
      .map(([settingId, value]) => ({ settingId, value }));
  } else {
    settingsArr = [];
  }
  // 0) Minden részről szabadítsd meg a külső [ ] jeleket
  const cleaned = rawParts.map((p) => p.replace(/^\[|\]$/g, "").trim());
  if (
    cleaned.length === 1 &&
    cleaned[0].includes(",") &&
    // nincsen benne i(...) makró és nincsen ?: conditional
    !/\bi\(/.test(cleaned[0]) &&
    !cleaned[0].includes("?")
  ) {
    // használd a splitTopLevel-t, hogy jól kezelje akár belső zárójelezést is
    const partsFromOne = splitTopLevel(cleaned[0]);
    // csak az első három tengelyt tartsd meg
    cleaned.splice(0, 1, ...partsFromOne.slice(0, 3));
  }
  // 1) Most már a cleaned-et használd tovább (helyettesítsd a parts-et ezzel)
  const parts = cleaned.slice();
  while (parts.length < 3) parts.push("0");

  // DEBUG, ha kell:
  console.log(
    "[EI] parts(cleaned):",
    parts,
    "count:",
    count,
    "parentSize:",
    parentSize
  );

  // 2) A normál feldolgozás változatlan:
  const norm = parts.map((p) => p.trim());

  // 3) Settings + dims egy objektumba
  console.log("parentsettings: ", parentSettings);
  const S = Object.fromEntries(
    settingsArr.map(({ settingId, value }) => {
      // if it parses as a plain number, store Number; otherwise keep the raw string
      const asNum = Number(value);
      return [
        settingId,
        String(value).trim() === String(asNum) ? asNum : value
      ];
    })
  );
  S.width = parentSize[0];
  S.height = parentSize[1];
  S.depth = parentSize[2];

  // 4) Megnézzük tengelyenként, hogy i(...)‐e, vagy konstans
  const axes = norm.map((part) => {
    if (part.startsWith("i(") && part.endsWith(")")) {
      // bontsuk fel start‐re és end‐re
      const [startExpr, endExpr, mode] = splitIMacroArgs(part);
      // tisztítsuk meg (makrócsere, változóhelyettesítés)
      const cleanStart = evaluateFormula(startExpr, S);
      const cleanEnd = evaluateFormula(endExpr, S);
      // JS‐eval
      const start = Number(new Function(`return (${cleanStart})`)());
      const end = Number(new Function(`return (${cleanEnd})`)());

      if (mode === "c") {
        if (count === 1) {
          return [(start + end) / 2];
        }
        const step = (end - start) / (count + 1);
        return Array.from({ length: count }, (_, i) => start + step * (i + 1));
      }

      if (count === 1) return [start];
      const step = (end - start) / (count - 1);
      return Array.from({ length: count }, (_, i) => start + step * i);
    }
    // ha nem i(...), akkor evalArr‐rel egyszer lekérjük, és ismételjük
    const v = evalArr(`[${part}]`, settingsArr, parentSize)[0] || 0;
    return Array(count).fill(v);
  });

  // 5) Összeállítjuk a [x,y,z] mátrixot
  return Array.from({ length: count }, (_, i) => [
    axes[0][i],
    axes[1][i],
    axes[2][i]
  ]);
}*/

// src/utils/evalArr.js — MÓDOSÍTOTT expandIterated (debug logokkal)

// src/utils/evalArr.js — ÚJ rácsos expandIterated (részletes logokkal)

// CARTESIAN rács + részletes logok, „iterál” kulcsszóval
export function expandIterated(rawParts, count, parentSettings, parentSize) {
  const LOG = "[EI] iterál";

  // --- settings normalizálás ---
  let settingsArr;
  if (Array.isArray(parentSettings)) {
    settingsArr = parentSettings;
  } else if (parentSettings && typeof parentSettings === "object") {
    settingsArr = Object.entries(parentSettings)
      .filter(([k]) => !["width", "height", "depth"].includes(k))
      .map(([settingId, value]) => ({ settingId, value }));
  } else {
    settingsArr = [];
  }

  // [x,y,z] darabolás
  const cleaned = rawParts.map((p) =>
    String(p)
      .replace(/^\[|\]$/g, "")
      .trim()
  );
  if (
    cleaned.length === 1 &&
    cleaned[0].includes(",") &&
    !cleaned[0].includes("?")
  ) {
    const partsFromOne = splitTopLevel(cleaned[0]);
    cleaned.splice(0, 1, ...partsFromOne.slice(0, 3));
  }
  while (cleaned.length < 3) cleaned.push("0");
  const parts = cleaned.slice(0, 3);

  // Settings → objektum + dimenziók
  const S = Object.fromEntries(
    settingsArr.map(({ settingId, value }) => {
      const asNum = Number(value);
      return [
        settingId,
        String(value).trim() === String(asNum) ? asNum : value
      ];
    })
  );
  S.width = Array.isArray(parentSize) ? parentSize[0] : 0;
  S.height = Array.isArray(parentSize) ? parentSize[1] : 0;
  S.depth = Array.isArray(parentSize) ? parentSize[2] : 0;
  S.qty = count;

  console.groupCollapsed(`${LOG} – start`, { rawParts, parts, count, S });

  const numEval = (code) => {
    try {
      const cleaned = evaluateFormula(String(code), S);
      return Number(new Function(`return (${cleaned})`)());
    } catch {
      return 0;
    }
  };

  const guessStepsFromAxis = (expr) => {
    // keresünk bármely (*id*) makrót; ha S[id] pozitív egész → lépésszám
    const re = /\(\*(\d+)\*\)/g;
    let m;
    while ((m = re.exec(expr))) {
      const n = Number(S[m[1]]);
      if (Number.isFinite(n) && n > 0) return Math.round(n);
    }
    return null;
  };

  const parseAxis = (raw) => {
    const part = String(raw).trim();
    if (part.startsWith("[") || part.includes("?")) {
      try {
        const s = evaluateFormula(part, S);
        const v = new Function(`return (${s});`)();
        const n = Array.isArray(v) ? Number(v[0]) || 0 : Number(v) || 0;
        return { kind: "const", value: n };
      } catch {
        return { kind: "const", value: 0 };
      }
    }
    if (part.startsWith("i(") && part.endsWith(")")) {
      const [startExpr, endExpr, modeRaw] = splitIMacroArgs(part);
      const start = numEval(startExpr);
      const end = numEval(endExpr);

      const token = String(modeRaw || "")
        .trim()
        .toLowerCase();
      let mode = "inclusive"; // alap: végpontokkal együtt
      if (["c", "center", "true"].includes(token)) {
        mode = "center"; // n darab szakasz közepe
      } else if (
        ["inner", "mid", "between", "b", "belso", "belso"].includes(token)
      ) {
        mode = "inner"; // n belső pont, egyenlő margókkal
      }

      const guessedSteps = guessStepsFromAxis(part);
      return { kind: "iter", start, end, mode, guessedSteps, raw: part };
    }
    /* const v =
      evalArr(`[${part}]`, settingsArr, [S.width, S.height, S.depth])[0] || 0;
    return { kind: "const", value: v };*/
    return { kind: "const", value: numEval(part) };
  };

  const axes = parts.map(parseAxis);
  const iterIdx = axes
    .map((a, i) => (a.kind === "iter" ? i : -1))
    .filter((i) => i >= 0);
  const N = Math.max(1, parseInt(count, 10) || 1);
  const steps = [1, 1, 1];

  // ——— lépésszámok meghatározása ———
  if (iterIdx.length === 1) {
    const i = iterIdx[0];
    steps[i] = axes[i].guessedSteps ?? N;
  } else if (iterIdx.length === 2) {
    const [i, j] = iterIdx;
    let gi = axes[i].guessedSteps;
    let gj = axes[j].guessedSteps;

    // Fallback #1: ha a makrók már eltűntek, próbáljuk S[35] (oszlop) és S[34] (sor) értékeket
    const s35 = Number(S["35"]);
    const s34 = Number(S["34"]);
    if (
      (!gi || !gj) &&
      Number.isFinite(s35) &&
      s35 > 0 &&
      Number.isFinite(s34) &&
      s34 > 0
    ) {
      // csak ha mindkettő iter tengely épp X (0) és Y (1)
      if (i === 0 && j === 1) {
        gi = s35;
        gj = s34;
      } else if (i === 1 && j === 0) {
        gi = s34;
        gj = s35;
      }
    }

    // Fallback #2: keressünk S-ből olyan (a,b) párt, amire a*b == N
    if (!gi || !gj) {
      const smallInts = Object.values(S)
        .map(Number)
        .filter((n) => Number.isInteger(n) && n > 1 && n <= N);
      let a = null,
        b = null;
      outer: for (const x of smallInts) {
        for (const y of smallInts) {
          if (x * y === N) {
            a = x;
            b = y;
            break outer;
          }
        }
      }
      if (a && b) {
        gi = gi || a;
        gj = gj || b;
      }
    }

    if (gi && gj) {
      steps[i] = gi;
      steps[j] = gj;
    } else if (gi) {
      steps[i] = gi;
      steps[j] = Math.max(1, Math.ceil(N / gi));
    } else if (gj) {
      steps[j] = gj;
      steps[i] = Math.max(1, Math.ceil(N / gj));
    } else {
      // végső fallback: négyzetes felosztás
      const nx = Math.max(1, Math.floor(Math.sqrt(N)));
      const ny = Math.max(1, Math.ceil(N / nx));
      steps[i] = nx;
      steps[j] = ny;
    }
  } else if (iterIdx.length === 3) {
    const g = iterIdx.map((k) => axes[k].guessedSteps || null);
    let n = Math.max(1, Math.floor(Math.cbrt(N)));
    steps[iterIdx[0]] = g[0] || n;
    steps[iterIdx[1]] = g[1] || n;
    const used = steps[iterIdx[0]] * steps[iterIdx[1]];
    steps[iterIdx[2]] = g[2] || Math.max(1, Math.ceil(N / Math.max(1, used)));
  }

  // tengely-sorozatok
  const seqFor = (def, n) => {
    if (def.kind !== "iter") return Array(n).fill(def.value);

    const { start, end, mode = "inclusive" } = def;
    const span = end - start;

    // n<=1: érthető defaultok
    if (n <= 1) {
      if (mode === "center") return [(start + end) / 2];
      if (mode === "inner") return [start + span / 2];
      return [start]; // inclusive
    }

    if (mode === "center") {
      // n szakasz -> szakaszközéppontok (end-exclusive)
      const step = span / n;
      const first = start + step / 2;
      return Array.from({ length: n }, (_, i) => first + i * step);
    } else if (mode === "inner") {
      // n belső pont, egyenlő margóval: (k+1)/(n+1)
      const step = span / (n + 1);
      return Array.from({ length: n }, (_, i) => start + (i + 1) * step);
    } else {
      // "inclusive": végpontokkal együtt
      const step = n === 1 ? 0 : span / (n - 1);
      return Array.from({ length: n }, (_, i) => start + i * step);
    }
  };

  const X = seqFor(axes[0], steps[0]);
  const Y = seqFor(axes[1], steps[1]);
  const Z = seqFor(axes[2], steps[2]);

  // KARTÉZIÁN SZORZAT: X (oszlop) a leggyorsabb, aztán Y (sor), majd Z
  const total =
    Math.max(1, steps[0]) * Math.max(1, steps[1]) * Math.max(1, steps[2]);
  const M = Math.min(N, total);
  const out = [];
  for (let idx = 0; idx < M; idx++) {
    const ix = idx % steps[0];
    const iy = Math.floor(idx / steps[0]) % steps[1];
    const iz = Math.floor(idx / (steps[0] * steps[1])) % steps[2];
    out.push([X[ix], Y[iy], Z[iz]]);
  }

  console.table(out);
  console.groupEnd();

  // ha minden konstans volt, duplikáljunk N-szer
  if (iterIdx.length === 0) {
    const x = axes[0].kind === "const" ? axes[0].value : 0;
    const y = axes[1].kind === "const" ? axes[1].value : 0;
    const z = axes[2].kind === "const" ? axes[2].value : 0;
    return Array.from({ length: N }, () => [x, y, z]);
  }

  return out;
}

function splitTopLevel(str) {
  const parts = [];
  let depthPar = 0,
    depthBr = 0,
    last = 0;
  for (let i = 0; i < str.length; i++) {
    const ch = str[i];
    if (ch === "(") depthPar++;
    else if (ch === ")") depthPar--;
    else if (ch === "[") depthBr++;
    else if (ch === "]") depthBr--;
    else if (ch === "," && depthPar === 0 && depthBr === 0) {
      parts.push(str.slice(last, i).trim());
      last = i + 1;
    }
  }
  parts.push(str.slice(last).trim());
  return parts;
}

export function extractRawList(str) {
  if (!str) return [];
  const s = String(str).trim();

  // ha tényleg nested lista a formátum (két vagy több "[…]" egymás után)
  if (s.startsWith("[[") && s.endsWith("]]")) {
    // vágjuk le az első/utolsó szögletes zárójelet
    const inner = s.slice(1, -1);
    // szedjük szét top‑levelben
    const raws = splitTopLevel(inner);
    // és szabadítsuk meg az egyes részeket a külső [ ]
    return raws.map((r) => r.replace(/^\[|\]$/g, ""));
  }

  // ha van ?, passzoljuk tovább egyben
  if (s.includes("?")) return [s];

  // egyébként csak a "[…]" blokkokat gyűjtsük ki
  const matches = s.match(/\[[^\]]*\]/g) || [];
  return matches.length > 0 ? matches : [s];
}

export function evaluateFormula(formula, settingsObj) {
  let expr = String(formula)
    .trim()
    .replace(/[+\-*/\s]+$/, "");

  // Egységesen a resolveSettingValue-t használd (kezeli a láncolt makrót és az @-os változatot is)
  const settingsArr = Array.isArray(settingsObj)
    ? settingsObj
    : Object.entries(settingsObj || {}).map(([settingId, value]) => ({
        settingId: String(settingId),
        value: String(value)
      }));

  expr = expr.replace(
    /\(\*(\d+(?:@\d+)?)\.(cur|sum|len|idx|idx0|bef\.sum|aft\.sum)\*\)/g,
    (_, key, mode) => {
      try {
        const resolved = JSON.parse(resolveSettingValue(settingsArr, key));
        const list = typeof resolved === "string" ? JSON.parse(resolved) : resolved;
        if (!Array.isArray(list) || list.some(Array.isArray)) return "0";
        const values = list.map((value) => Number(value) || 0);
        const modes = { cur: values[0] || 0, sum: values.reduce((sum, value) => sum + value, 0),
          len: values.length, idx: 1, idx0: 0, "bef.sum": 0,
          "aft.sum": values.slice(1).reduce((sum, value) => sum + value, 0) };
        return String(modes[mode]);
      } catch { return "0"; }
    }
  );
  expr = expr.replace(
    /\(\*(\d+(?:@\d+)?)\*\)/g,
    (_, key) => resolveSettingValue(settingsArr, key) // számként ad vissza, vagy idézőjeles stringet, ha nem szám
  );

  expr = expr.replace(/\b(width|height|depth|qty)\b/g, (_, v) =>
    String(settingsObj && settingsObj[v] != null ? settingsObj[v] : 0)
  );

  return expr;
}
function splitTopLevelArgs(src) {
  const out = [];
  let depth = 0;
  let cur = "";
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (ch === "(" || ch === "[" || ch === "{") depth++;
    else if (ch === ")" || ch === "]" || ch === "}") depth--;
    if (ch === "," && depth === 0) {
      out.push(cur.trim());
      cur = "";
    } else {
      cur += ch;
    }
  }
  if (cur.trim()) out.push(cur.trim());
  return out;
}

function isIdent(ch) {
  return /[A-Za-z0-9_]/.test(ch);
}

// s( ... ) → a példány-index (ix) szerinti alternatíva kiválasztása, körbeforgással
function replaceSCallsForIndex(src, ix) {
  let out = "";
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    const prev = i > 0 ? src[i - 1] : "";

    // token-határon álló s( ... ) (ne kapjuk el pl. az "abs(" véget)
    if (
      ch === "s" &&
      !isIdent(prev) &&
      i + 1 < src.length &&
      src[i + 1] === "("
    ) {
      let j = i + 2; // '(' utáni első karakter
      let depth = 0;
      const argStart = j;
      let foundEnd = false;

      while (j < src.length) {
        const c = src[j];
        if (c === "(" || c === "[" || c === "{") depth++;
        else if (c === ")" || c === "]" || c === "}") {
          if (depth === 0) {
            // s( ARG ) lezárása
            const inner = src.slice(argStart, j);
            const args = splitTopLevelArgs(inner);
            const pick = args.length > 0 ? args[ix % args.length] : "0";
            out += `(${pick})`;
            i = j; // a for-ciklus i++-ja átugorja a ')'-t
            foundEnd = true; // megvan a záró
            break;
          } else {
            depth--;
          }
        }
        j++;
      }

      if (!foundEnd) out += ch; // hibás zárójelezésnél békén hagyjuk
    } else {
      out += ch;
    }
  }
  return out;
}

export function parseConditionalPosition(
  raw,
  parentSettings,
  parentSize,
  count = 1
) {
  count = parseInt(count, 10);
  if (isNaN(count) || count < 1) count = 1;
  raw = stripOuterParentheses(raw);
  const unwrapped = unwrapVector(raw);
  if (unwrapped !== raw && (unwrapped.trim().startsWith("[") || (splitVectorParts(unwrapped).length === 1 && splitConditionalExpression(unwrapped)))) {
    return parseConditionalPosition(unwrapped, parentSettings, parentSize, count);
  }
  const vectors = splitVectorParts(raw);
  if (vectors.length > 1 && vectors.every((part) => {
    const expression = stripOuterParentheses(part);
    return expression.startsWith("[") || Boolean(splitConditionalExpression(expression));
  })) {
    return vectors.flatMap((vector) => parseConditionalPosition(vector, parentSettings, parentSize, 1));
  }
  // Expressions produced by the vector editor can branch around the whole
  // vector or inside an individual axis, at any nesting depth.
  const conditional = splitConditionalExpression(raw);
  if (conditional && !/\bs\s*\(/.test(raw)) {
    const settings = Array.isArray(parentSettings)
      ? Object.fromEntries(parentSettings.map(({ settingId, value }) => [settingId, value]))
      : { ...parentSettings };
    const context = { ...settings, width: parentSize[0], height: parentSize[1], depth: parentSize[2], qty: count };
    const condition = evaluateFormula(conditional.condition, context);
    const chosen = new Function(`return (${condition})`)()
      ? conditional.whenTrue : conditional.whenFalse;
    return parseConditionalPosition(chosen, parentSettings, parentSize, count);
  }
  const vectorParts = splitVectorParts(unwrapVector(raw));
  if (vectorParts.length === 3 && !vectorParts.some((part) => part.startsWith("[")) && raw.includes("?") && !/\bs\s*\(/.test(raw)) {
    return expandIterated(vectorParts, count, parentSettings, parentSize);
  }
  // ——————————————————————————————————————

  // 1) Mátrix-vágás
  const raws = extractRawList(raw);
  const hasS = /\bs\s*\(/.test(String(raw));
  if (hasS) {
    const N = Math.max(1, parseInt(count, 10) || 1);
    const out = [];
    for (let ix = 0; ix < N; ix++) {
      const perIndexExpr = replaceSCallsForIndex(String(raw), ix);
      // Itt N-nel futtatunk, hogy az i(...) a teljes sorozatot adja,
      // majd az ix-edik elemet vesszük ki.
      const seq = parseConditionalPosition(
        perIndexExpr,
        parentSettings,
        parentSize,
        N
      );
      out.push(seq[ix] || seq[0] || [0, 0, 0]);
    }
    return out;
  }
  // 2) Egyszerű többsoros lista?
  if (!raw.includes("?") && raws.length > 1) {
    const coords = raws.map((str) =>
      evalArr(`[${str}]`, parentSettings, parentSize)
    );
    return coords;
  }

  // 3) Csak egy elem, nincs ?: → expandIterated
  if (!raw.includes("?")) {
    // vágjuk le a külső [ ]
    const inner = raw.replace(/^\[|\]$/g, "");
    // osszuk a három tengelyre top-level vesszőknél
    const parts = splitTopLevel(inner)
      .map((s) => s.trim())
      .slice(0, 3);
    return expandIterated(parts, count, parentSettings, parentSize);
  }

  // 4) Van ?: → bontás
  const inner = raw.replace(/^\[|\]$/g, "");
  let depthPar = 0,
    depthBr = 0,
    qPos = -1,
    cPos = -1;
  for (let i = 0; i < inner.length; i++) {
    const ch = inner[i];
    if (ch === "(") depthPar++;
    else if (ch === ")") depthPar--;
    else if (ch === "[") depthBr++;
    else if (ch === "]") depthBr--;
    else if (ch === "?" && depthPar === 0 && depthBr === 0) qPos = i;
    else if (ch === ":" && depthPar === 0 && depthBr === 0 && qPos >= 0) {
      cPos = i;
      break;
    }
  }
  if (qPos < 0 || cPos < 0) {
    console.warn("[PCP] malformed conditional, falling back");
    return expandIterated(raws, count, parentSettings, parentSize);
  }

  const condExpr = inner.slice(0, qPos).trim();
  const trueBranch = inner.slice(qPos + 1, cPos).trim();
  const falseBranch = inner.slice(cPos + 1).trim();

  // 5) Makró- és dimenzió-helyettesítés
  const S = Object.fromEntries(
    parentSettings.map(({ settingId, value }) => {
      const num = Number(value);
      return [settingId, String(num) === String(value) ? num : value];
    })
  );
  S.width = parentSize[0];
  S.height = parentSize[1];
  S.depth = parentSize[2];

  const expr = evaluateFormula(condExpr, S);
  const condVal = new Function(`return (${expr})`)();

  // 7) Végül ezeket átadjuk az expandIterated‑nek
  const branchRaw = condVal ? trueBranch : falseBranch;
  {
    const br = branchRaw.trim();
    if (br.includes("?")) {
      // Ha i(...) szerepel, biztosan nem JS – rekurzió
      if (/\bi\(/.test(br)) {
        return parseConditionalPosition(br, parentSettings, parentSize, count);
      }
      // Különben megpróbálhatjuk gyorsan JS-sel
      const evalStr = evaluateFormula(br, S);
      try {
        const res = new Function(`return (${evalStr});`)();
        if (Array.isArray(res)) {
          if (Array.isArray(res[0])) {
            console.log("res:", res);

            const flat = Array.isArray(res[0][0]) ? res[0] : res;
            console.log("flat:", flat);
            return flat.map((r) => r.slice(0, 3).map((v) => +v || 0));
          }
          return [res.slice(0, 3).map((v) => +v || 0)];
        }
      } catch {
        // ha bukik, esünk vissza a rekurzióra
        return parseConditionalPosition(br, parentSettings, parentSize, count);
      }
    }
  }
  // ha nested lista (két vagy több "[…]" egymás után), akkor egyszerű lista‑eval
  if (branchRaw.trim().startsWith("[[") && branchRaw.trim().endsWith("]]")) {
    // extractRawList visszaadja az egyes "[…]" blokkokat belül
    const raws = extractRawList(branchRaw);
    // minden sorra hívd az evalArr-t
    return raws.map((str) => evalArr(`[${str}]`, parentSettings, parentSize));
  }
  // egyébként mint eddig: split → expandIterated
  const chosen1 = branchRaw.replace(/^\[|\]$/g, "");
  const parts1 = splitTopLevel(chosen1)
    .map((p) => p.trim())
    .slice(0, 3);
  return expandIterated(parts1, count, parentSettings, parentSize);
}
